import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CompanyStatuses, STATUS_REASON_REQUIRED, messages, type CompanyStatus } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/common/status-badge';
import { cn } from '@/lib/utils';

/** 営業状況の変更。「失注」「対象外」は理由必須 */
export const StatusChangeDialog = ({
  open,
  onOpenChange,
  companyId,
  currentStatus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  currentStatus: CompanyStatus;
}) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<CompanyStatus>(currentStatus);
  const [reason, setReason] = useState('');

  const reasonRequired = STATUS_REASON_REQUIRED.includes(status);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/companies/${companyId}/status`, { status, reason: reason || undefined }),
    onSuccess: () => {
      toast.success(`営業状況を「${CompanyStatuses.label(status)}」に変更しました`, {
        // 変更後トーストで取り消せる（8 秒）
        duration: 8000,
        action: {
          label: messages.company.statusChangeUndo,
          onClick: () => {
            void api
              .post(`/companies/${companyId}/status`, {
                status: currentStatus,
                reason: '変更を取り消しました',
              })
              .then(() => {
                toast.success('変更を取り消しました');
                void queryClient.invalidateQueries({ queryKey: qk.company(companyId) });
              });
          },
        },
      });
      void queryClient.invalidateQueries({ queryKey: qk.company(companyId) });
      void queryClient.invalidateQueries({ queryKey: qk.companyTimeline(companyId) });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      onOpenChange(false);
      setReason('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.company.changeStatus}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <fieldset>
            <legend className="mb-2 text-body font-semibold">変更後の状況</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {CompanyStatuses.list.map((item) => (
                <label
                  key={item.value}
                  className={cn(
                    'flex min-h-touch cursor-pointer items-center gap-2 rounded-control border p-2',
                    status === item.value ? 'border-2 border-foreground bg-muted' : 'border-border',
                  )}
                >
                  <input
                    type="radio"
                    name="status"
                    value={item.value}
                    checked={status === item.value}
                    onChange={() => setStatus(item.value)}
                    className="size-4 accent-foreground"
                  />
                  <StatusBadge kind="company_status" value={item.value} />
                </label>
              ))}
            </div>
          </fieldset>

          <Field
            label={messages.common.reason}
            required={reasonRequired}
            hint={reasonRequired ? undefined : '任意。履歴に記録されます'}
          >
            {(props) => (
              <Input
                {...props}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonRequired ? '断りの返信があったため' : ''}
              />
            )}
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || status === currentStatus || (reasonRequired && !reason.trim())}
            disabledReason={
              status === currentStatus
                ? '現在と同じ状況です'
                : reasonRequired && !reason.trim()
                  ? '理由を入力してください'
                  : undefined
            }
          >
            変更する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
